import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// ── DATA ──────────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    id: '01',
    title: 'Digital Evidence Forensics',
    short: 'Evidence Forensics',
    icon: '◈',
    desc: 'Device imaging workflows, metadata extraction, social media preservation, and cloud account analysis. Full chain-of-custody documentation generated automatically.',
    tags: ['Chain of Custody', 'Metadata', 'Social Preservation', 'Device Imaging'],
  },
  {
    id: '02',
    title: 'Financial Discovery & Tracing',
    short: 'Financial Discovery',
    icon: '◉',
    desc: 'Trust account analysis, asset identification from bank statements and tax documents, transaction pattern detection, and hidden income modeling.',
    tags: ['Trust Analysis', 'Asset Tracing', 'Transaction Patterns', 'Hidden Income'],
  },
  {
    id: '03',
    title: 'Document Intelligence',
    short: 'Document Intel',
    icon: '◫',
    desc: 'AI-powered OCR on scanned documents, automated exhibit numbering and organization, contradiction detection across filings, and deposition prep generation.',
    tags: ['OCR', 'Exhibit Numbering', 'Contradiction Detection', 'Depo Prep'],
  },
  {
    id: '04',
    title: 'Case Timeline Construction',
    short: 'Timeline Builder',
    icon: '◑',
    desc: 'Visual chronology built from documents, texts, emails, and records. Interactive timeline view with export for courtroom presentation in PDF or PPTX.',
    tags: ['Chronology', 'Interactive View', 'PDF Export', 'Court-Ready'],
  },
  {
    id: '05',
    title: 'Filing Preparation Tools',
    short: 'Filing Prep',
    icon: '◧',
    desc: 'AI-assisted drafting utilities, PDF conversion, digital signing, and jurisdiction-aware rule-checking tools. Practice Book formatting templates for Connecticut Superior Court. Technology and templates — not legal services.',
    tags: ['AI Drafting Tools', 'PDF Convert', 'Digital Signing', 'CT Practice Book'],
  },
  {
    id: '06',
    title: 'Opposing Counsel Analytics',
    short: 'OC Analytics',
    icon: '◐',
    desc: 'Track opposing counsel filings, analyze patterns, and surface judicial preference data from historical docket analysis across thousands of cases.',
    tags: ['Filing Patterns', 'Judicial Preferences', 'Docket Analysis', 'Intelligence'],
  },
  {
    id: '07',
    title: 'Deposition & Cross-Exam Prep',
    short: 'Depo / Cross Prep',
    icon: '◍',
    desc: 'Inconsistency detection across witness statements, auto-generated question frameworks, and impeachment material organization. Voice-based mock cross-examination via ARIA.',
    tags: ['Inconsistency Detection', 'Question Frameworks', 'Impeachment', 'Voice Prep'],
  },
  {
    id: '08',
    title: 'Regulatory & Records Research',
    short: 'Records Research',
    icon: '◌',
    desc: 'Public records integration, corporate entity tracing for complex ownership structures, property record investigation, and regulatory filing analysis.',
    tags: ['Public Records', 'Entity Tracing', 'Property Records', 'Regulatory'],
  },
  {
    id: '09',
    title: 'Evidence Chain Management',
    short: 'Evidence Vault',
    icon: '◎',
    desc: 'End-to-end evidence lifecycle tracking with authentication protocols, integrity verification via cryptographic hashing, and admissibility analysis checklists.',
    tags: ['Lifecycle Tracking', 'Authentication', 'Hash Verification', 'Admissibility'],
  },
];

const STATS = [
  { value: '3.4M+', label: 'Docket Entries Indexed' },
  { value: '9', label: 'Integrated Service Modules' },
  { value: '40+', label: 'Jurisdiction Rule Sets' },
  { value: '< 2s', label: 'Case Law Verification Time' },
];

const NAV_LINKS = ['Services', 'Platform', 'Attorneys', 'About', 'Contact'];
const NAV_EXTERNAL = [
  { label: 'Builder', href: '/builder' },
  { label: 'DDV Portal', href: 'https://ddv.arprose.com' },
];

// ── COMPONENTS ────────────────────────────────────────────────────────────────

function ARMark({ size = 48, glow = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: glow ? 'drop-shadow(0 0 12px rgba(0,212,255,0.6))' : undefined }}
    >
      <polygon
        points="24,2 44,13 44,35 24,46 4,35 4,13"
        fill="none"
        stroke="#00d4ff"
        strokeWidth="1.5"
      />
      <polygon
        points="24,7 39,15.5 39,32.5 24,41 9,32.5 9,15.5"
        fill="rgba(0,212,255,0.06)"
        stroke="rgba(0,212,255,0.3)"
        strokeWidth="0.75"
      />
      <text
        x="24"
        y="30"
        textAnchor="middle"
        fontFamily="Bebas Neue, sans-serif"
        fontSize="18"
        fill="#00d4ff"
        letterSpacing="1"
      >AR</text>
    </svg>
  );
}

function DisclosureBanner() {
  return (
    <div className="disclosure-banner" role="region" aria-label="Legal disclosure">
      <span className="disclosure-text">
        // IMPORTANT: A.R. PRO SE / A2BE INC. IS NOT A LAW FIRM AND DOES NOT PROVIDE LEGAL SERVICES OR LEGAL ADVICE.
        INFORMATION HEREIN IS FOR GENERAL INFORMATIONAL PURPOSES ONLY.
        CONSULT A LICENSED ATTORNEY IN YOUR JURISDICTION FOR LEGAL ADVICE.
      </span>
    </div>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
    setOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <div className="nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <ARMark size={36} />
          <div className="nav-brand-text">
            <span className="nav-name">A.R. PRO SE</span>
            <span className="nav-sub">STRATEGIC OPERATIONS</span>
          </div>
        </div>
        <div className={`nav-links ${open ? 'open' : ''}`}>
          {NAV_LINKS.map(l => (
            <button key={l} className="nav-link" onClick={() => scrollTo(l)}>{l}</button>
          ))}
          {NAV_EXTERNAL.map(l => (
            <a key={l.label} href={l.href} className="nav-link" style={{ textDecoration: 'none' }}>{l.label}</a>
          ))}
          <a href="mailto:info@arprose.com" className="nav-cta">Get Access</a>
        </div>
        <button className="hamburger" onClick={() => setOpen(!open)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

function HeroSection() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 2200);
    return () => clearInterval(t);
  }, []);

  const rotating = ['Evidence.', 'Discovery.', 'Strategy.', 'Intelligence.'];

  return (
    <section className="hero" id="hero">
      <div className="hero-grid-bg" aria-hidden="true" />
      <div className="hero-scanline" aria-hidden="true" />

      <div className="hero-inner">
        <div className="hero-badge">
          <span className="badge-dot" />
          <span>ARIA Platform — Now Operational</span>
        </div>

        <div className="hero-mark-large">
          <ARMark size={120} glow />
        </div>

        <h1 className="hero-h1">
          Cryptographically Verifiable<br />
          Litigation Operations.<br />
          <span className="hero-accent">Patent Pending.</span>
        </h1>

        <div className="hero-rotating">
          <span className="rotating-prefix">// Powered for </span>
          <span className="rotating-word" key={tick}>
            {rotating[tick % rotating.length]}
          </span>
        </div>

        <p className="hero-sub">
          Patent-pending technology infrastructure built by a self-represented litigant —
          for self-represented litigants and the independent attorneys who support them.
          ARIA: cryptographically verifiable research and document workflows.
          Not a law firm. Not legal advice.
        </p>

        <div className="hero-actions">
          <a href="mailto:info@arprose.com" className="btn-primary">
            Request Platform Access
          </a>
          <button
            className="btn-ghost"
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
          >
            View Services ↓
          </button>
        </div>

        <div className="hero-stats">
          {STATS.map(s => (
            <div className="stat-item" key={s.label}>
              <span className="stat-value">{s.value}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PatentPendingBanner() {
  return (
    <section className="patent-banner" id="patent">
      <div className="patent-inner">
        <div className="patent-tag">// PATENT PENDING</div>
        <div className="patent-claims">
          <div className="patent-stat">
            <span className="patent-num">88</span>
            <span className="patent-label">claims</span>
          </div>
          <span className="patent-divider">·</span>
          <div className="patent-stat">
            <span className="patent-num">8</span>
            <span className="patent-label">subsystems</span>
          </div>
          <span className="patent-divider">·</span>
          <div className="patent-stat">
            <span className="patent-num">2026·04·28</span>
            <span className="patent-label">filed</span>
          </div>
        </div>
        <p className="patent-body">
          USPTO Provisional Patent Application filed under 37 CFR § 1.10. Cryptographic verification chain ·
          Multi-channel electronic service · Voice-call verification · Postal-mail clearinghouse ·
          Self-administered forensic prediction · Blockchain-anchored integrity · Post-quantum cryptographic primitives.
        </p>
        <p className="patent-disclaimer">
          Provisional applications are not examined and do not confer enforceable patent rights until and unless a non-provisional matures to grant.
        </p>
      </div>
    </section>
  );
}

function AttorneyPartnerSection() {
  return (
    <section className="partner-section" id="attorneys">
      <div className="section-inner">
        <div className="section-header">
          <span className="section-tag" style={{ color: '#f0b429' }}>// ATTORNEY PARTNER PROGRAM</span>
          <h2 className="section-title">
            A Network of Independent Attorneys.<br />
            <span className="hero-accent" style={{ color: '#f0b429' }}>Forthcoming.</span>
          </h2>
          <p className="section-desc">
            A.R. Pro Se / A2BE Inc. is actively engaging with bar-admitted attorneys regarding a forthcoming
            network of independent attorneys offering limited-scope representation (LSR) and unbundled legal
            services to consumers. The forthcoming network is in active development.
          </p>
        </div>
        <div className="partner-pillars">
          <div className="partner-pillar">
            <div className="pillar-tag">// WHEN THE NETWORK LAUNCHES</div>
            <ul className="pillar-list">
              <li>All legal services in the network <strong>will be provided by independent attorneys</strong> admitted to practice in the relevant jurisdiction.</li>
              <li>Engagement letters will be entered between consumers and individual attorneys. <strong>A2BE Inc. will not be a party to any attorney-client relationship</strong>.</li>
              <li>A2BE Inc.'s role will be limited to <strong>technology, customer acquisition, intake, payment processing, and administrative services</strong>.</li>
              <li>Each launch jurisdiction will be cleared for compliance with applicable bar rules — including unauthorized-practice, fee-splitting (Rule 5.4 / equivalents), and advertising rules — before operating.</li>
              <li>The network is subject to <strong>ongoing review by bar-admitted ethics counsel</strong>.</li>
            </ul>
          </div>
          <div className="partner-pillar partner-status">
            <div className="pillar-tag pillar-tag-status">// CURRENT STATUS</div>
            <p className="status-body">
              The network is <strong>not yet live</strong>. No consumer can today obtain legal services through
              A.R. Pro Se / A2BE Inc. Consumers seeking legal representation should consult a licensed attorney
              admitted in their jurisdiction directly.
            </p>
            <div className="pillar-tag" style={{ marginTop: '1.75rem' }}>// INTERESTED ATTORNEYS</div>
            <p className="status-body">
              Bar-admitted attorneys interested in the forthcoming network may inquire at{' '}
              <a href="mailto:attorneys@arprose.com" className="partner-link">attorneys@arprose.com</a>.
              Inquiries are not engagement proposals; the network is in development and not yet accepting matters.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const [active, setActive] = useState(null);

  return (
    <section className="services-section" id="services">
      <div className="section-inner">
        <div className="section-header">
          <span className="section-tag">// 09 MODULES</span>
          <h2 className="section-title">Service Intelligence Suite</h2>
          <p className="section-desc">
            Every capability a litigant needs — from first subpoena to final brief.
            Modular, precise, court-ready.
          </p>
        </div>

        <div className="services-grid">
          {SERVICES.map((svc) => (
            <div
              key={svc.id}
              className={`service-card ${active === svc.id ? 'active' : ''}`}
              onClick={() => setActive(active === svc.id ? null : svc.id)}
            >
              <div className="svc-header">
                <span className="svc-id">{svc.id}</span>
                <span className="svc-icon">{svc.icon}</span>
              </div>
              <h3 className="svc-title">{svc.title}</h3>
              <p className={`svc-desc ${active === svc.id ? 'visible' : ''}`}>{svc.desc}</p>
              <div className="svc-tags">
                {svc.tags.map(t => <span key={t} className="svc-tag">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformSection() {
  return (
    <section className="platform-section" id="platform">
      <div className="section-inner">
        <div className="platform-grid">
          <div className="platform-text">
            <span className="section-tag">// ARIA PLATFORM</span>
            <h2 className="section-title">Your AI Litigation Intelligence Layer</h2>
            <p className="platform-body">
              ARIA is the voice and intelligence engine behind every A.R. Pro Se module.
              Natural language. Real-time document drafting. Jurisdiction-aware.
              Multi-LLM architecture with self-hosted Kimi K2.5 on-metal for data privacy, integrated with frontier APIs for maximum capability.
            </p>
            <ul className="platform-features">
              {[
                'Shepardization & case law verification via CourtListener API',
                'Real-time statute validation against state legislative databases',
                'Multi-source citation checking — LexisNexis, Westlaw, CourtListener',
                'AI hallucination detection — flags fabricated citations before filing',
                'Practice Book rule compliance with jurisdiction-aware formatting',
                'Integrated with Gmail, Google Drive, and e-filing workflows',
                'Multi-agent architecture across 9 service modules',
                'Cryptographically signed PDFs for court submission',
              ].map(f => (
                <li key={f} className="platform-feature">
                  <span className="feature-check">◈</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a href="mailto:info@arprose.com" className="btn-primary" style={{ display: 'inline-block', marginTop: '2rem' }}>
              Request ARIA Demo
            </a>
          </div>

          <div className="platform-terminal">
            <div className="terminal-bar">
              <span className="t-dot t-red" />
              <span className="t-dot t-yellow" />
              <span className="t-dot t-green" />
              <span className="t-title">aria — shepardize & verify</span>
            </div>
            <div className="terminal-body">
              <div className="t-line"><span className="t-prompt">aria&gt;</span> <span className="t-cmd">verify all citations in today's filing package</span></div>
              <div className="t-line t-out">⟳ Scanning 9 documents for case law, statutes, and rules...</div>
              <div className="t-line t-out">⟳ Cross-referencing CourtListener API + caselaw.db...</div>
              <div className="t-line t-out">&nbsp;</div>
              <div className="t-line t-out t-success">✓ 19 case citations — all verified &amp; good law</div>
              <div className="t-line t-out t-success">✓ 14 statutes — all verified via cga.ct.gov</div>
              <div className="t-line t-out t-success">✓ 7 Practice Book sections — all current</div>
              <div className="t-line t-out">&nbsp;</div>
              <div className="t-line t-out t-gold">⚑ Gronbach v. Gronbach, 189 Conn. App. 360 — DOES NOT EXIST</div>
              <div className="t-line t-out" style={{color:'#ff6b6b',paddingLeft:'1.2rem'}}>↳ AI hallucination detected. Removed from filing.</div>
              <div className="t-line t-out t-gold">⚑ Luce v. United Technologies, 247 Conn. 126 — wrong context</div>
              <div className="t-line t-out" style={{color:'#ff6b6b',paddingLeft:'1.2rem'}}>↳ Workers comp case. Not applicable. Removed.</div>
              <div className="t-line t-out">&nbsp;</div>
              <div className="t-line"><span className="t-prompt">aria&gt;</span> <span className="t-cmd">artifact check — any scaffolding left?</span></div>
              <div className="t-line t-out t-success">✓ No &#123;CITED CASE LAW&#125; placeholders</div>
              <div className="t-line t-out t-success">✓ No HTML tags, blank dates, or TODO notes</div>
              <div className="t-line t-out t-success">✓ All 9 filings signed, dated, court-ready</div>
              <div className="t-line t-out">&nbsp;</div>
              <div className="t-line t-out" style={{color:'#00d4ff'}}>━━ 73 citations verified │ 2 hallucinations caught │ 0 artifacts ━━</div>
              <div className="t-line t-blink"><span className="t-prompt">aria&gt;</span> <span className="t-cursor">█</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="about-section" id="about">
      <div className="section-inner">
        <div className="about-grid">
          <div className="about-quote-col">
            <blockquote className="about-quote">
              "The courtroom should not be a place where resources determine outcomes.
              Every party deserves access to the same analytical firepower —
              the evidence should speak, not the billing rate."
            </blockquote>
            <cite className="about-cite">— Founder, A.R. Pro Se</cite>
          </div>
          <div className="about-text-col">
            <span className="section-tag">// ORIGIN</span>
            <h2 className="section-title">Built by Someone Who Needed It</h2>
            <p className="about-body">
              A.R. Pro Se was built by a self-represented litigant navigating a complex
              high-asset family court case. Unable to find adequate tools, the founder
              built the infrastructure himself — subpoena targeting, statistical docket
              analysis, evidence forensics, and AI-powered filing preparation.
            </p>
            <p className="about-body">
              What began as a personal toolkit is now a platform for every party who
              walks into court without a $1,000-per-hour attorney and refuses to lose
              because of it.
            </p>
            <div className="about-disclaimer">
              <strong>LEGAL NOTICE.</strong>
              <ul className="disclaimer-list">
                <li>A.R. Pro Se / A2BE Inc. is <strong>not a law firm</strong>.</li>
                <li>A.R. Pro Se / A2BE Inc. <strong>does not provide legal services or legal advice</strong>.</li>
                <li>A.R. Pro Se / A2BE Inc. is <strong>not a substitute for a licensed attorney</strong>.</li>
                <li>Use of this site or any A.R. Pro Se tool <strong>does not create an attorney-client relationship</strong>.</li>
                <li>Information herein is for <strong>general informational purposes only</strong>.</li>
              </ul>
              For legal advice on your matter, consult a licensed attorney admitted to practice in the jurisdiction where you have a legal matter.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="contact-section" id="contact">
      <div className="section-inner contact-inner">
        <div className="section-header">
          <span className="section-tag">// SECURE ACCESS</span>
          <h2 className="section-title">Get Platform Access</h2>
          <p className="section-desc">
            A.R. Pro Se is currently available by application. Contact us to discuss
            your case and determine fit.
          </p>
        </div>
        <div className="contact-cards">
          <a href="mailto:info@arprose.com" className="contact-card">
            <span className="contact-icon">◉</span>
            <span className="contact-label">Email</span>
            <span className="contact-value">info@arprose.com</span>
          </a>
          <div className="contact-card">
            <span className="contact-icon">◈</span>
            <span className="contact-label">Entity</span>
            <span className="contact-value">A2BE Inc. / ARProSe</span>
          </div>
          <div className="contact-card">
            <span className="contact-icon">◫</span>
            <span className="contact-label">Jurisdiction</span>
            <span className="contact-value">Connecticut Superior Court</span>
          </div>
        </div>
        <a href="mailto:info@arprose.com" className="btn-primary btn-large">
          Request Access →
        </a>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <ARMark size={32} />
          <div>
            <div className="footer-name">A.R. PRO SE</div>
            <div className="footer-tagline">Strategic Operations</div>
          </div>
        </div>
        <div className="footer-links">
          {NAV_LINKS.map(l => (
            <button
              key={l}
              className="footer-link"
              onClick={() => document.getElementById(l.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })}
            >
              {l}
            </button>
          ))}
          {NAV_EXTERNAL.map(l => (
            <a key={l.label} href={l.href} className="footer-link" style={{ textDecoration: 'none' }}>
              {l.label}
            </a>
          ))}
        </div>
        <div className="footer-copy">
          <div className="footer-disclaimers">
            <p><strong>Not a law firm.</strong> A.R. Pro Se Strategic Operations and A2BE Inc. are not law firms and do not provide legal services or legal advice. Information on this site is for general informational purposes only.</p>
            <p><strong>No attorney-client relationship.</strong> Use of this site or any related tool does not create an attorney-client relationship.</p>
            <p><strong>No outcome guarantee.</strong> Nothing on this site is a representation or warranty of any litigation outcome.</p>
            <p><strong>Patent pending.</strong> References to patent-pending status refer to USPTO Provisional Application filed April 28, 2026 (88 claims, 8 subsystems, 37 CFR § 1.10). Provisional applications are not examined and do not confer enforceable patent rights until and unless a non-provisional matures to grant.</p>
          </div>
          <span className="footer-copyright">© 2026 A2BE Inc. — All Rights Reserved</span>
        </div>
      </div>
    </footer>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <div className="app">
      <Navbar />
      <HeroSection />
      <PatentPendingBanner />
      <ServicesSection />
      <PlatformSection />
      <AttorneyPartnerSection />
      <AboutSection />
      <ContactSection />
      <DisclosureBanner />
      <Footer />
    </div>
  );
}
